// @ts-nocheck

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { toast } from 'sonner';
import { Bot, Pencil, Phone, Plus, RefreshCw, Trash2, Workflow } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ConfirmDialog } from '@/components/feedback/ConfirmDialog';
import FlowChartDialog from './builder-components/FlowChartDialog';
import HeadingInfo from './builder-components/HeadingInfo';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import {
  AGENT_TYPES,
  AGENT_TYPE_LABELS,
  AI_NAME_MAX_LENGTH,
} from '@/utilities/aiAgentsStore';
import { listAgents, getAgent, deleteAgent, getAiAgentLicence, saveDraft, getDraft, deleteDraft } from '@/service/aiAgentApi';
import { getUser } from '@/utilities/setting';
import { MODULE_ACL, USER_ROLES, ROUTES } from '@/utilities/constant';
import { hasPermission } from '@/utilities/helpers';
import { parseModuleOperations } from '@/utilities/voicePlanAcl';

const STATUS_LABELS: Record<string, string> = {
  draft: 'Draft',
  published: 'Published',
  disabled: 'Disabled',
  active: 'Active',
};

const STATUS_VARIANTS: Record<string, any> = {
  draft: 'secondary',
  published: 'success',
  disabled: 'destructive',
  active: 'success',
};

const mapAgentRow = (row: any) => {
  const isDraft = !row.agent_id;
  const rawStatus = row.is_delete
    ? 'disabled'
    : String(row.status || (isDraft ? 'draft' : 'published')).toLowerCase();
  return {
    id: row.agent_id || `draft-${row.id}`,
    draftId: row.id,
    agentId: row.agent_id || null,
    isDraft,
    name: row.name,
    agent_type: row.bot_type === 'voice_bot' ? AGENT_TYPES.VOICE : row.bot_type || AGENT_TYPES.VOICE,
    status: rawStatus,
    extension_number: row.extension_number || '',
    created_by_name: row.created_by_name || '',
    created_by_username: row.created_by_username || '',
    created_at: row.created_at || '',
    updated_at: row.updated_at || '',
    flow_chart_data: row.flow_chart_data || null,
  };
};

const AgentsListPage = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState<any[]>([]);
  const [loggedInUserACL, setLoggedInUserACL] = useState<any>({});
  const [aclLoaded, setAclLoaded] = useState(false);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newAgentName, setNewAgentName] = useState('');
  const [creatingAgent, setCreatingAgent] = useState(false);
  const [checkingLicence, setCheckingLicence] = useState(false);
  const [loadingRows, setLoadingRows] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<any>(null);
  const [deleting, setDeleting] = useState(false);
  const [viewingFlow, setViewingFlow] = useState<any>(null);

  const user = getUser();
  const isSuperAdmin = user?.role === USER_ROLES.SUPERADMIN;

  const refreshAgents = () => {
    setLoadingRows(true);
    listAgents(user?.id)
      .then((agents) => setRows(agents.map(mapAgentRow)))
      .catch((err) => toast.error(typeof err === 'string' ? err : 'Failed to load agents'))
      .finally(() => setLoadingRows(false));
  };

  useEffect(() => {
    const applyAcl = () => {
      const ops = getUser()?.acl_settings?.module_operations;
      if (!ops) return;
      try {
        setLoggedInUserACL(typeof ops === 'string' ? parseModuleOperations(ops) : ops);
        setAclLoaded(true);
      } catch (e) {
        console.log('Failed to parse ACL', e);
      }
    };

    applyAcl();
    window.addEventListener('acl-refreshed', applyAcl);
    refreshAgents();
    return () => window.removeEventListener('acl-refreshed', applyAcl);
  }, []);

  const isCallCentreAdmin = user?.role === USER_ROLES.CALL_CENTRE_ADMIN;
  const aclAllows = (op: string) =>
    !aclLoaded || hasPermission(loggedInUserACL, MODULE_ACL.AI_AGENT, op);

  const canRead =
    isSuperAdmin ||
    isCallCentreAdmin ||
    !aclLoaded ||
    hasPermission(loggedInUserACL, MODULE_ACL.AI_AGENT, 'read') ||
    hasPermission(loggedInUserACL, MODULE_ACL.AI_AGENT, 'view_permission');
  const canAdd = !isSuperAdmin && (isCallCentreAdmin || aclAllows('add'));
  const canEdit = !isSuperAdmin && (isCallCentreAdmin || aclAllows('edit'));
  const canDelete = !isSuperAdmin && (isCallCentreAdmin || aclAllows('delete'));

  const openCreateModal = async () => {
    if (checkingLicence) return;
    setCheckingLicence(true);
    try {
      const licence = await getAiAgentLicence();
      if (!licence?.allowed) {
        toast.error(
          licence?.message ||
            'AI Agent licence is not available. Ask your parent to allocate AI Agent licences.',
        );
        return;
      }
      setNewAgentName('');
      setShowCreateModal(true);
    } catch (err) {
      toast.error(typeof err === 'string' ? err : 'Unable to check AI Agent licence.');
    } finally {
      setCheckingLicence(false);
    }
  };

  const openBuilderFor = async (agent: any) => {
    try {
      if (agent.isDraft) {
        const fullDraft = await getDraft(agent.draftId);
        const params = new URLSearchParams({
          draftId: String(agent.draftId),
          name: fullDraft?.name || agent.name,
          type: agent.agent_type,
        });
        navigate(`/${ROUTES.AI_AGENT.BUILDER}?${params.toString()}`, { state: { agent: fullDraft } });
        return;
      }
      const fullAgent = await getAgent(agent.agentId, user?.id);
      const params = new URLSearchParams({
        id: agent.agentId,
        name: fullAgent?.name || agent.name,
        type: agent.agent_type,
      });
      navigate(`/${ROUTES.AI_AGENT.BUILDER}?${params.toString()}`, { state: { agent: fullAgent } });
    } catch (err) {
      toast.error(typeof err === 'string' ? err : 'Failed to load agent');
    }
  };

  const handleCreate = async () => {
    const name = newAgentName.trim();

    if (!name) {
      toast.error('Agent name is required');
      return;
    }
    if (name.length > AI_NAME_MAX_LENGTH) {
      toast.error(`Agent name must be ${AI_NAME_MAX_LENGTH} characters or fewer`);
      return;
    }

    setCreatingAgent(true);
    try {
      const draft = await saveDraft({
        agent_name: name,
        bot_type: 'voice_bot',
        user_id: String(user?.id ?? ''),
        ...(user?.company_id ? { reseller_id: String(user.company_id) } : {}),
      });

      setShowCreateModal(false);

      navigate(
        `/${ROUTES.AI_AGENT.BUILDER}?${new URLSearchParams({
          draftId: String(draft.id),
          name,
          type: AGENT_TYPES.VOICE,
        }).toString()}`,
      );
    } catch (err) {
      toast.error(typeof err === 'string' ? err : 'Failed to create agent');
    } finally {
      setCreatingAgent(false);
    }
  };

  const requestDelete = (agent: any) => {
    setPendingDelete(agent);
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      if (pendingDelete.isDraft) {
        await deleteDraft(pendingDelete.draftId);
        toast.success('Draft deleted');
      } else {
        await deleteAgent(pendingDelete.agentId, user?.id);
        toast.success('Agent deleted successfully');
      }
      setPendingDelete(null);
      refreshAgents();
    } catch (err) {
      toast.error(typeof err === 'string' ? err : 'Failed to delete agent');
    } finally {
      setDeleting(false);
    }
  };

  const columns = useMemo<ColumnDef<any>[]>(() => {
    const cols: ColumnDef<any>[] = [
      {
        id: 'name',
        accessorFn: (row: any) => row.name ?? '',
        header: 'Name',
        cell: ({ row }) =>
          isSuperAdmin ? (
            <span className="font-medium text-foreground">{row.original.name}</span>
          ) : (
            <button
              type="button"
              className="font-medium text-primary hover:underline"
              onClick={() => openBuilderFor(row.original)}
            >
              {row.original.name}
            </button>
          ),
      },
      {
        id: 'agent_type',
        accessorFn: (row: any) => AGENT_TYPE_LABELS[row.agent_type] || row.agent_type || '',
        header: 'Type',
        cell: ({ row }) => (
          <Badge variant="outline">
            {AGENT_TYPE_LABELS[row.original.agent_type] || row.original.agent_type}
          </Badge>
        ),
      },
      {
        id: 'status',
        accessorFn: (row: any) => row.status ?? '',
        header: 'Status',
        cell: ({ row }) => {
          const status = row.original.status;
          return (
            <Badge variant={STATUS_VARIANTS[status] || 'secondary'}>
              {STATUS_LABELS[status] || status}
            </Badge>
          );
        },
      },
      {
        id: 'extension_number',
        accessorFn: (row: any) => row.extension_number ?? '',
        header: 'Extension',
        cell: ({ row }) => (
          <span className="text-sm text-foreground">{row.original.extension_number || '—'}</span>
        ),
      },
      
      ...(isSuperAdmin
        ? [
            {
              id: 'created_by',
              accessorFn: (row: any) => row.created_by_name || row.created_by_username || '',
              header: 'Created By',
              cell: ({ row }) => {
                const name = row.original.created_by_name;
                const username = row.original.created_by_username;
                if (!name && !username) return <span className="text-xs text-muted-foreground">—</span>;
                return (
                  <div className="leading-tight">
                    <div className="text-sm text-foreground">{name || username}</div>
                    {name && username && name !== username && (
                      <div className="text-[11px] text-muted-foreground">@{username}</div>
                    )}
                  </div>
                );
              },
            },
          ]
        : []),
      {
        id: 'created_at',
        accessorFn: (row: any) => (row.created_at ? new Date(row.created_at).toLocaleString() : ''),
        header: 'Created At',
        cell: ({ row }) => (
          <span className="text-xs text-muted-foreground">
            {row.original.created_at ? new Date(row.original.created_at).toLocaleString() : '—'}
          </span>
        ),
      },
      {
        id: 'updated_at',
        accessorFn: (row: any) => (row.updated_at ? new Date(row.updated_at).toLocaleString() : ''),
        header: 'Updated',
        cell: ({ row }) => (
          <span className="text-xs text-muted-foreground">
            {row.original.updated_at ? new Date(row.original.updated_at).toLocaleString() : '—'}
          </span>
        ),
      }
    ];

    // Superadmin (or anyone without edit/delete rights) gets a read-only listing.
    if (!canEdit && !canDelete) return cols;

    cols.push(
      {
        id: 'actions',
        header: 'Options',
        size: 90,
        enableHiding: false,
        enableSorting: false,
        enableGlobalFilter: false,
        cell: ({ row }) => (
          <div className="flex items-center gap-1">
            {canEdit && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  openBuilderFor(row.original);
                }}
                className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary ring-1 ring-inset ring-primary/20 transition-colors hover:bg-primary/15"
                title="Edit"
                aria-label="Edit"
              >
                <Pencil className="h-3.5 w-3.5" />
              </button>
            )}
            {canDelete && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  requestDelete(row.original);
                }}
                className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-rose-100 text-rose-700 ring-1 ring-inset ring-rose-200 transition-colors hover:bg-rose-200 dark:bg-rose-950/40 dark:text-rose-300"
                title="Delete"
                aria-label="Delete"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        ),
      },
    );

    return cols;
  }, [canEdit, canDelete, isSuperAdmin]);

  if (aclLoaded && !canRead) {
    return (
      <FillPage>
        <PageHeader title="AI Agents" />
        <div className="rounded-md border border-border bg-muted/30 p-8 text-center text-sm text-muted-foreground">
          No read permission found in assigned ACL.
        </div>
      </FillPage>
    );
  }

  return (
    <FillPage>
      <PageHeader
        title="AI Agents"
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" onClick={refreshAgents} disabled={loadingRows} title="Refresh">
              <RefreshCw className={`h-4 w-4 ${loadingRows ? 'animate-spin' : ''}`} />
            </Button>
            {canAdd && (
              <Button onClick={openCreateModal} disabled={checkingLicence}>
                <Plus className="h-4 w-4" />
                {checkingLicence ? 'Checking licence…' : 'New Agent'}
              </Button>
            )}
          </div>
        }
      />

      <DataTable
        tableId="ai-agent:agents:list:v1"
        columns={columns}
        data={rows}
        isLoading={loadingRows}
        searchPlaceholder="Search agents…"
        emptyTitle="No agents yet"
        emptyDescription="Create your first AI agent to get started."
        getRowId={(row: any) => String(row.id)}
        initialColumnPinning={{ right: ['actions'] }}
        pageSize={10}
        exportFilename="ai-agents.csv"
      />

      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Create AI Agent</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="agent-name" className="flex items-center gap-1.5">
                Name<span className="text-destructive">*</span>
                <HeadingInfo text="The display name of this voice agent, used to identify it in your dashboard and agent list." />
              </Label>
              <Input
                id="agent-name"
                placeholder="e.g. Support Bot"
                value={newAgentName}
                onChange={(e) => setNewAgentName(e.target.value)}
                maxLength={AI_NAME_MAX_LENGTH}
                autoFocus
              />
              <p className="text-right text-xs text-muted-foreground">
                {newAgentName.length}/{AI_NAME_MAX_LENGTH}
              </p>
            </div>

            <div className="space-y-1.5">
              <Label>Select Agent Type</Label>

              <div className="rounded-lg border-2 border-primary bg-primary/5 p-4">
                <div className="flex items-start gap-3">
                  <Phone className="mt-0.5 h-5 w-5 text-primary" />
                  <div>
                    <p className="text-sm font-medium text-foreground">Voice Agent</p>
                    <p className="text-xs text-muted-foreground">
                      Conversational spoken interactions over phone.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-start gap-2 rounded-md bg-muted/50 p-3 text-xs text-muted-foreground">
              <Bot className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              This setting cannot be changed once selected.
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)} disabled={creatingAgent}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={creatingAgent}>
              {creatingAgent ? 'Creating…' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(o) => {
          if (!o) setPendingDelete(null);
        }}
        title={pendingDelete?.isDraft ? 'Discard this draft?' : 'Delete this agent?'}
        description={
          <>
            Agent{' '}
            <span className="font-medium text-foreground">{pendingDelete?.name}</span> will be
            {pendingDelete?.isDraft
              ? ' discarded and removed from this list.'
              : ' disabled and removed from this list. This can be restored later if needed.'}
          </>
        }
        confirmLabel="Delete"
        tone="destructive"
        loading={deleting}
        onConfirm={confirmDelete}
      />

    </FillPage>
  );
};

export default AgentsListPage;
